import { Children, useEffect, useRef, useState, type ReactNode } from 'react';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { Button } from 'antd';

export function clampEditorWidth(percent: number, width: number) {
  const minimum = Math.min(360, width * 0.4);
  const maximum = width - Math.min(320, width * 0.4) - 10;
  return Math.max(
    Math.max(20, (minimum / width) * 100),
    Math.min(Math.min(80, (maximum / width) * 100), percent),
  );
}

export function EditorPage({
  title,
  actions,
  onBack,
  children,
}: {
  title: ReactNode;
  actions: ReactNode;
  onBack: () => void;
  children: ReactNode;
}) {
  return (
    <section className="content-editor-page">
      <header className="content-editor-page-header">
        <div>
          <Button icon={<ArrowLeftOutlined />} onClick={onBack}>
            返回列表
          </Button>
          <strong>{title}</strong>
        </div>
        {actions}
      </header>
      {children}
    </section>
  );
}

export function EditorWorkspace({ children }: { children: ReactNode }) {
  const panes = Children.toArray(children);
  const workspaceRef = useRef<HTMLDivElement>(null);
  const [percent, setPercent] = useState(() => {
    const stored = Number(localStorage.getItem('luobo-editor-width'));
    return stored >= 20 && stored <= 80 ? stored : 42;
  });
  const percentRef = useRef(percent);
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef<{ pointerId: number; left: number; width: number } | null>(null);
  const setWidth = (next: number) => {
    percentRef.current = next;
    setPercent(next);
  };
  const rememberWidth = () =>
    localStorage.setItem('luobo-editor-width', String(percentRef.current));
  useEffect(() => {
    const workspace = workspaceRef.current;
    if (!workspace) return;
    const observer = new ResizeObserver(() => {
      if (workspace.clientWidth > 0)
        setWidth(clampEditorWidth(percentRef.current, workspace.clientWidth));
    });
    observer.observe(workspace);
    return () => observer.disconnect();
  }, []);
  return (
    <div
      className={`content-edit-workspace${dragging ? ' is-resizing' : ''}`}
      ref={workspaceRef}
      style={{ gridTemplateColumns: `${percent}% 10px minmax(0, 1fr)` }}
    >
      {panes[0]}
      <div
        className="content-edit-divider"
        role="separator"
        aria-label="调整编辑与预览区域宽度"
        aria-orientation="vertical"
        aria-valuemin={20}
        aria-valuemax={80}
        aria-valuenow={Math.round(percent)}
        tabIndex={0}
        onPointerDown={(event) => {
          if (!event.isPrimary || event.button !== 0) return;
          const workspace = workspaceRef.current;
          if (!workspace) return;
          const bounds = workspace.getBoundingClientRect();
          event.currentTarget.setPointerCapture(event.pointerId);
          dragRef.current = { pointerId: event.pointerId, left: bounds.left, width: bounds.width };
          setDragging(true);
          event.preventDefault();
        }}
        onPointerMove={(event) => {
          const drag = dragRef.current;
          if (!drag || event.pointerId !== drag.pointerId) return;
          setWidth(clampEditorWidth(((event.clientX - drag.left) / drag.width) * 100, drag.width));
        }}
        onPointerUp={(event) => {
          if (event.currentTarget.hasPointerCapture(event.pointerId))
            event.currentTarget.releasePointerCapture(event.pointerId);
          dragRef.current = null;
          setDragging(false);
          rememberWidth();
        }}
        onPointerCancel={() => {
          dragRef.current = null;
          setDragging(false);
        }}
        onLostPointerCapture={() => {
          dragRef.current = null;
          setDragging(false);
        }}
        onDoubleClick={() => {
          const width = workspaceRef.current?.clientWidth;
          setWidth(width ? clampEditorWidth(42, width) : 42);
          rememberWidth();
        }}
        onKeyDown={(event) => {
          if (!['ArrowLeft', 'ArrowRight', 'Home'].includes(event.key)) return;
          const width = workspaceRef.current?.clientWidth;
          if (!width) return;
          event.preventDefault();
          setWidth(
            clampEditorWidth(
              event.key === 'Home' ? 42 : percent + (event.key === 'ArrowLeft' ? -2 : 2),
              width,
            ),
          );
          rememberWidth();
        }}
      />
      {panes[1]}
    </div>
  );
}
